import { Component, Output, EventEmitter, Input, inject } from '@angular/core';
import { ModalFormContainerComponent } from '../../ui/modal-form-container/modal-form-container.component';
import { InputFieldComponent } from '../../ui/inputField/inputField.component';
import { CustomizableButtonComponent } from '../../ui/customizable-button/customizable-button.component';
import { GroupSelectionInputComponent } from '../../inputs/group-selection-input/group-selection-input.component';
import { countries } from '../../../../utils/countriesList';
import {
  ReactiveFormsModule,
  Validators,
  FormBuilder,
  AbstractControl,
} from '@angular/forms';
import { IContactFormDataKeys } from '../create-contact-form/create-contact-form.component';
import { IContact, IOrganization, ITag } from '../../../types/contactModule';
import { AssignTagsModalComponent } from '../../tables/assign-tags-modal/assign-tags-modal.component';
import { AssignOrganizationsTableComponent } from '../../tables/assign-organizations-modal/assign-organizations-modal.component';
import {
  NgxMatInputTelComponent,
  ngxMatInputTelValidator,
} from 'ngx-mat-input-tel';

import { ContactsService } from '../../../services/contacts/contacts.service';
import {
  getCallingCode,
  getParsedCountryNumber,
  parseFlexiblePhoneNumber,
} from '../../../../utils/utils';

@Component({
  selector: 'edit-contact-modal',
  standalone: true,
  imports: [
    ModalFormContainerComponent,
    InputFieldComponent,
    GroupSelectionInputComponent,
    CustomizableButtonComponent,
    ReactiveFormsModule,
    AssignTagsModalComponent,
    AssignOrganizationsTableComponent,
    NgxMatInputTelComponent,
  ],
  templateUrl: './edit-contact-modal.component.html',
  styleUrls: ['../create-contact-form/contact-form.css', './edit-contact-modal.component.css'],
})
export class EditContactModalComponent {
  constructor(private builder: FormBuilder) {}
  contactService = inject(ContactsService);

  countries = countries;

  selectedOrganizations: IOrganization[] = [];
  selectedTags: ITag[] = [];
  loading: boolean = false;

  // modal controls
  showAddOrganizationModal: boolean = false;
  showAddTagModal: boolean = false;

  openAddOrganizationModal() {
    this.showAddOrganizationModal = true;
  }
  closeAddOrganizationModal() {
    this.showAddOrganizationModal = false;
  }
  openAddTagModal() {
    this.showAddTagModal = true;
  }
  closeAddTagModal() {
    this.showAddTagModal = false;
  }

  @Input({ required: true }) selectedContact!: IContact | null;
  @Output() closeModalEvent = new EventEmitter();
  contactForm = this.builder.group({
    firstName: ['', Validators.required],
    lastName: [''],
    emailAddress: ['', [Validators.email, ]],
    primaryContactNumber: ['', Validators.required],
    gender: ['', ],
    birthDate: ['', ],
    country: ['', ],
    zipCode: ['', ],
    city: [''],
  });

  submitted: boolean = false;
  /** Why the last save failed, in words the person can act on. */
  saveError: string = '';

  getFormInput(name: IContactFormDataKeys) {
    return this.contactForm.get(name);
  }

  get emailErrorMsg() {
    const email = this.getFormInput('emailAddress');
    if (email?.errors?.['email']) {
      return 'Enter an email address like name@example.com';
    }
    return '';
  }

  get phoneErrorMsg() {
    const phone = this.contactForm.controls.primaryContactNumber;
    if (!phone.invalid) return '';
    return phone.errors?.['required'] ? 'Enter a phone number' : 'Enter a valid phone number';
  }

  // update the organization selection in the parent cmpnt
  updateSelectedOrganizations(selections: IOrganization[]) {
    this.selectedOrganizations = [...(selections ?? [])];
  }

  // update the organization selection in the parent cmpnt
  updateSelectedTags(selections: ITag[]) {
    this.selectedTags = [...(selections ?? [])];
  }

  removeSelectedOrganization(organization: any) {
    const passedOrg = organization as IOrganization;
    this.selectedOrganizations = this.selectedOrganizations.filter(
      (org) => org.id !== organization.id
    );
  }

  removeSelectedTag(tag: any) {
    const passedTag = tag as ITag;
    this.selectedTags = this.selectedTags.filter(
      (tag) => tag.id !== passedTag.id
    );
  }

  async handleSubmit() {
    this.submitted = true;
    this.saveError = '';
    if (!this.contactForm.value.zipCode) {
      // Dial code from the stored country, else from the number itself.
      let dialCode = '';
      try {
        dialCode = this.selectedContact?.country ? getCallingCode(this.selectedContact.country) : '';
      } catch {
        dialCode = '';
      }
      if (!dialCode || dialCode === '+undefined') {
        const parsed = parseFlexiblePhoneNumber(this.contactForm.value.primaryContactNumber);
        dialCode = parsed ? `+${parsed.countryCallingCode}` : '';
      }
      this.contactForm.patchValue({ zipCode: dialCode });
    }
    if (this.contactForm.valid) {
      this.loading = true;
      this.contactService
        .updateContact(
          {
            ...this.selectedContact,
            ...(this.contactForm.value as IContact),
            // "Not set": no gender, rather than an empty string the API cannot read as one.
            gender: this.contactForm.value.gender || null,
            companyEntities: this.selectedOrganizations,
            entityTags: this.selectedTags,
          },
          this.selectedContact?.['uuid'] as string
        )
        .subscribe({
          next: () => {
            this.loading = false;
            this.closeModal();
          },
          error: (err) => {
            this.loading = false;
            const message = `${err?.error?.message ?? err?.error?.title ?? err?.message ?? ''}`;
            this.saveError = /duplicate/i.test(message)
              ? 'Another contact already has this phone number.'
              : 'The changes were not saved. Check the details and try again.';
          },
          complete: () => {
            this.loading = false;
          },
        });
    }
    // console.log(this.contactForm, 'the form object');
  }

  countryChanged(country: any) {
    this.contactForm.patchValue({
      zipCode: `+${country.dialCode}`,
    });
  }

  private preferredFor: string | null = null;
  private preferredList: string[] = ['bw', 'za'];

  /** The contact's own country first, then Botswana (the default) and South Africa, without repeats. */
  get preferredCountries(): string[] {
    const code = this.getCountryCode;
    if (code !== this.preferredFor) {
      this.preferredFor = code;
      this.preferredList = [...new Set([code, 'bw', 'za'].filter(Boolean))];
    }
    return this.preferredList;
  }

  get getCountryCode() {
    let result=''
    const parsedNumber = getParsedCountryNumber(this.selectedContact?.primaryContactNumber);
    if(parsedNumber?.country){
      result = parsedNumber.country.toLocaleLowerCase()
    }else{
      if(!this.selectedContact?.countryCode){
        return result
      }
      result = this.selectedContact?.countryCode!.toLocaleLowerCase()!
    }
    return result
  }

  closeModal() {
    this.closeModalEvent.emit();
  }

  ngOnChanges() {
    this.contactForm.patchValue({
      ...(this.selectedContact as typeof this.contactForm.value),
      // Empty values select the "Not set" options instead of a blank select.
      gender: this.selectedContact?.gender ?? '',
      country: this.selectedContact?.country ?? '',
    });
    this.selectedOrganizations = [...((this.selectedContact?.companyEntities ?? []) as IOrganization[])];
    this.selectedTags = [...((this.selectedContact?.entityTags ?? []) as ITag[])];
  }
}
